import { HostingEnvironment } from '@opengov/psp-contract';
import { RouteRegistryId, type RouteRegistryEntry } from '../types';
import {
  getHostingEnvironmentDomain,
  resolveDevModuleUrl,
  resolvePspHostedRemoteModuleUrl,
} from './utils';
import { NotificationsSettingsAccessManagementPage } from '../pages/Notifications/NotificationsSettingsAccessManagementPage';
import { adminSettingsModuleUrl } from './adminSettingsModule';
import { GAB_APPS_MOUNT_PATH, gabCoreModuleUrl } from './gabCoreModule';
import { FeatureFlagKey } from '../hooks/useFeatureFlags';

/**
 * Route registry for ES module routes that don't appear in navigation.
 * Use this for utility pages accessible via dropdowns, links, or notifications.
 *
 * Unlike the app registry, routes here:
 * - Do NOT appear in the sidebar navigation
 * - Do NOT require entitlement checks
 * - Are accessible from anywhere (dropdowns, direct links, notifications)
 *
 * @example
 * // Adding a new route:
 * {
 *   id: RouteRegistryId.PROFILE_SETTINGS,
 *   basePath: '/profile-settings',
 *   description: 'User profile settings page',
 *   moduleRoutes: [
 *     {
 *       pattern: '/',
 *       resolver: (ctx) => {
 *         const domain = getHostingEnvironmentDomain(ctx.hostingEnvironment);
 *         return `https://profile.${domain}/psp-module/index.js`;
 *       },
 *     },
 *   ],
 * }
 */

/**
 * Path pattern for the shell-rendered notification settings page within the
 * Access Management product area. Used as both the route basePath below and
 * passed to the Access Management module via moduleConfig so the module can
 * build links without hardcoding the path.
 *
 * `:entityId` and `:userId` are path-to-regexp placeholders that callers must
 * substitute with real values when constructing a navigable URL.
 */
const NOTIFICATIONS_SETTINGS_PATH_PATTERN =
  '/access/:entityId/users/:userId/notifications-settings' as const;

export const routeRegistry: readonly RouteRegistryEntry[] = [
  {
    id: RouteRegistryId.ACCESS_MANAGEMENT,
    basePath: '/access',
    description:
      'Access Management application for user profile, user management, group management and api key management',
    moduleConfig: () => ({
      notificationsSettingsPath: NOTIFICATIONS_SETTINGS_PATH_PATTERN,
    }),
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          const integrationUrl = adminSettingsModuleUrl('/access/assets/access-management-psp.js');
          if (ctx.hostingEnvironment === HostingEnvironment.DEVELOPMENT) {
            return resolveDevModuleUrl('ACCESS_MANAGEMENT', integrationUrl);
          }
          return integrationUrl;
        },
      },
    ],
    unauthenticatedRoutes: [
      {
        pattern: '/login-redirect',
        resolver: (ctx) => {
          const integrationUrl = adminSettingsModuleUrl('/access/assets/access-management-psp.js');
          if (ctx.hostingEnvironment === HostingEnvironment.DEVELOPMENT) {
            return resolveDevModuleUrl('ACCESS_MANAGEMENT', integrationUrl);
          }
          return integrationUrl;
        },
      },
      {
        pattern: '/default_selection',
        resolver: (ctx) => {
          const integrationUrl = adminSettingsModuleUrl('/access/assets/access-management-psp.js');
          if (ctx.hostingEnvironment === HostingEnvironment.DEVELOPMENT) {
            return resolveDevModuleUrl('ACCESS_MANAGEMENT', integrationUrl);
          }
          return integrationUrl;
        },
      },
    ],
  },
  {
    id: RouteRegistryId.NOTIFICATIONS_SETTINGS,
    basePath: NOTIFICATIONS_SETTINGS_PATH_PATTERN,
    description:
      'Notification preferences — per-product notification settings for the active entity',
    component: NotificationsSettingsAccessManagementPage,
  },
  {
    id: RouteRegistryId.DATA_MANAGER,
    basePath: '/data_manager',
    description:
      'Data Manager and Workforce (link-only — no shell navigation entry). ' +
      'Workforce at /:entityId/workforce; all other /data_manager paths load Data Manager 2.0.',
    moduleRoutes: [
      {
        pattern: '/:entityId/workforce',
        resolver: (ctx) => {
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return 'http://localhost:10003/psp/budget-psp.js';
            case HostingEnvironment.INTEGRATION:
              return 'https://dy6148mdv4or.cloudfront.net/psp/budget-psp.js';
            case HostingEnvironment.STAGING:
              return 'https://d1gu8d8xe91oy6.cloudfront.net/psp/budget-psp.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://d32507ixvn160m.cloudfront.net/psp/budget-psp.js';
            default: {
              console.error(
                `Unhandled hosting environment for workforce route: ${ctx.hostingEnvironment}`
              );
              return '';
            }
          }
        },
      },
      {
        pattern: '/',
        resolver: (ctx) => {
          const integrationUrl = 'https://d3ifveumaembgm.cloudfront.net/psp/data-manager-app.js';
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return resolveDevModuleUrl('DATA_MANAGER', integrationUrl);
            case HostingEnvironment.INTEGRATION:
              return integrationUrl;
            case HostingEnvironment.STAGING:
              return 'https://d1308cq6znj5ca.cloudfront.net/psp/data-manager-app.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://d39cri7brgma92.cloudfront.net/psp/data-manager-app.js';
            default: {
              console.error(`Unhandled hosting environment: ${ctx.hostingEnvironment}`);
              return '';
            }
          }
        },
      },
    ],
  },
  {
    id: RouteRegistryId.ORG_REGISTRY,
    basePath: '/org-registry',
    description:
      'Org Registry application (link-only — no shell navigation entry). ' +
      'Mounted at /org-registry/<subdomain>; the module owns its internal router using ' +
      'baseRouterPath="/org-registry". The shell supplies activeEntity, auth0Config ' +
      '(getAccessToken for silent SSO), and the ShellBridge (shell.auth0) automatically ' +
      'via the standard RemoteAppShell mount path.',
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          // Org Registry module is served from the controlpanel domain alongside the
          // standalone SPA. The module build emits a fixed filename (no hash) so the
          // URL is stable across deploys: dist/module/index.js → /org-registry/module/index.js.
          // Engineers testing locally set VITE_DEV_MODULE_URL_ORG_REGISTRY in
          // .env.development.local (e.g. http://localhost:5180/index.js).
          const ogHostingDomain = getHostingEnvironmentDomain();
          const controlPanelUrl = `https://controlpanel.${ogHostingDomain}/org-registry/module/index.js`;
          if (ctx.hostingEnvironment === HostingEnvironment.DEVELOPMENT) {
            return resolveDevModuleUrl('ORG_REGISTRY', controlPanelUrl);
          }
          return controlPanelUrl;
        },
      },
    ],
  },
  {
    id: RouteRegistryId.FORMS,
    basePath: '/forms',
    description: 'Forms application for creating and managing forms',
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          switch (ctx.hostingEnvironment) {
            case HostingEnvironment.DEVELOPMENT:
              return 'http://localhost:10005/forms/assets/forms-app-psp.js';
            case HostingEnvironment.INTEGRATION:
              return 'https://d2utb6xirun2fr.cloudfront.net/psp/forms-app-psp.js';
            case HostingEnvironment.STAGING:
              return 'https://d1wizmsv3timfc.cloudfront.net/psp/forms-app-psp.js';
            case HostingEnvironment.PRODUCTION:
              return 'https://d2ztvseyy4nf2f.cloudfront.net/psp/forms-app-psp.js';
            default: {
              console.error(`Unhandled hosting environment: ${ctx.hostingEnvironment}`);
              return '';
            }
          }
        },
      },
    ],
  },
  {
    id: RouteRegistryId.ONBOARDING,
    basePath: '/onboarding',
    description: 'Onboarding experience — full-viewport remote module, no sidebar navigation',
    fullPage: true,
    moduleRoutes: [
      {
        pattern: '/',
        resolver: (ctx) => {
          // Hosted on the PSP origin under /remote-modules/. In local dev the
          // Vite `^/remote-modules` proxy forwards same-origin requests to hosted
          // PSP. Engineers testing a local onboarding build set
          // VITE_DEV_MODULE_URL_ONBOARDING in .env.development.local
          // (e.g. http://localhost:5177/index.js).
          const hostedUrl = resolvePspHostedRemoteModuleUrl('/remote-modules/onboarding/index.js');
          if (ctx.hostingEnvironment === HostingEnvironment.DEVELOPMENT) {
            return resolveDevModuleUrl('ONBOARDING', hostedUrl);
          }
          return hostedUrl;
        },
      },
    ],
  },
  /**
   * The one mount every gab-core app shares, as a route registry entry: the module loads once at
   * `/a` and its own router resolves the rest of the path, so an app's address is whatever its
   * entitlement URL names. It exists while `psp-ui-dynamic-gab-apps` is on.
   *
   * Being a registry entry is what makes it a mount like any other, rather than a special case:
   * - Sign-out finds the module by this id, so a module that opts in to ending its session when
   *   unmounted is reached after the user leaves `/a` or reloads.
   * - `App.tsx` renders it with one stable key, so moving between two apps, Back and Forward
   *   included, keeps one module instance. gab-core's `mount()` clears its query cache, so each
   *   remount is a cold start; a sidebar click on another app still remounts, because
   *   `RemoteAppShell` resets on any PSP-initiated navigation within its base path.
   * - Its refresh path is `/a` itself: with `psp-ui-lsf-page-refresh` on, leaving `/a` for another
   *   product reloads the page, while moving between two gab-core apps does not.
   * - `/a/*` exists even before the user's entitlements list any app, and for a path no entry owns
   *   (gab-core moves an app to its current address when a URL names an older one). gab-core
   *   decides whether the user can open the app and shows its own not-found page when not, so the
   *   route grants nothing the entitlement check would deny.
   *
   * The per-app entries in the app registry only supply the sidebar items (`navigationOnly`), so
   * `App.tsx` gives them no route of their own.
   */
  {
    id: RouteRegistryId.GAB_APPS,
    basePath: GAB_APPS_MOUNT_PATH,
    description: 'Every gab-core app, on one shared mount of the gab-core module',
    featureFlagKey: FeatureFlagKey.PSP_UI_DYNAMIC_GAB_APPS,
    moduleRoutes: [{ pattern: '/', resolver: gabCoreModuleUrl }],
    triggerPageRefreshOnNavigateAway: (ctx) =>
      !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
  },
  // TEST: Profile settings route - uses finance-dashboard bundle for testing
  // This demonstrates a route that loads an ES module but doesn't appear in sidebar
  ...(import.meta.env.DEV
    ? [
        {
          id: RouteRegistryId.PROFILE_SETTINGS,
          basePath: '/profile-settings',
          description: 'User profile settings page (TEST - uses finance-dashboard bundle)',
          moduleRoutes: [
            {
              pattern: '/',
              resolver: () => {
                // For testing, use the finance-dashboard bundle
                return 'http://localhost:3001/index.js';
              },
            },
          ],
        },
      ]
    : []),
] as const;
